import { ChangeEvent } from 'react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { SingleBetPropsType } from 'src/components/BetSlip/SingleBet';
import config from 'src/config';
import { AppState } from 'src/AppState/AppState';
import { parseInt } from 'lodash';
import { TypeId } from 'src_common/common/websocket2/type';
import { FreeBetType } from './BonusEngineFreeBets/FreeBetSelector';
import { BetBuilderPriceState, BetBuilderPriceStatusType } from './BetBuilderPriceState';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { BetBuilderBetPropsType } from './BetBuilderBet/BetBuilderBet';

const PropsInIO = t.interface({
    userId: t.number,
    bet: t.unknown,
    legs: t.unknown,
    currency: t.unknown,
    sportPriceLadders: t.unknown,
    competitions: t.unknown,
    assignedToMe: t.union([t.boolean, t.undefined, t.null]),
    focusedBet: t.unknown,
    selected: t.boolean,
    bonusEngineFreeBets: t.unknown,

    betWasChanged: t.unknown,
    onSelect: t.unknown,
    onUnselect: t.unknown,

    onLegChange: t.unknown,
    onChange: t.unknown,
    onRemove: t.unknown,
    onAddSplitBet: t.unknown,
    onRemoveSplitBet: t.unknown,
    onSetChangesStatus: t.unknown,
    onSetBetBuilderPriceStatus: t.unknown,
});

export type PropsInType = t.TypeOf<typeof PropsInIO>;
const isPropsInIO = createGuard(PropsInIO);

export const BetIO = t.interface({
    eachWay: t.union([t.undefined, t.null, t.boolean]),
    legs: t.array(t.string),
    type: t.string,
    uid: t.string,
    name: t.union([t.undefined, t.null, t.string]),
    numLines: t.union([t.undefined, t.null, t.number]),
    stakePerLine: t.union([t.number, t.string, t.null]),
    potentialReturnsAt: t.union([t.number, t.undefined, t.null]),
    potentialReturns: t.union([t.number, t.undefined, t.null]),
    errors: t.union([t.undefined, t.null, t.array(t.unknown)]),
    isSplitted: t.union([t.undefined, t.null, t.boolean]),
    maxStake: t.union([t.undefined, t.null, t.number]),
    freebet: t.union([t.undefined, t.null, t.boolean]),
    freebetCredits: t.union([t.undefined, t.null, t.unknown, t.array(t.unknown)]),
    freebetRemarks: t.union([t.undefined, t.null, t.unknown]),
    freeBets: t.union([t.undefined, t.null, t.array(t.string)]),
    price: t.union([t.null, t.number, t.string, t.undefined]),

    isReferred: t.union([t.boolean, t.undefined, t.null]),
    // referredStatus: t.union([t.undefined, t.string, t.null]),
    // channel: t.union([t.undefined, t.string, t.null]),
    // comment: t.union([t.undefined, t.string, t.null]),
    // correlationID: t.union([t.undefined, t.string, t.null]),
    // country: t.union([t.undefined, t.string, t.null]),
    // isExpired: t.union([t.undefined, t.boolean, t.null]),
    // affiliate: t.union([t.undefined, t.null]),
    // ip: t.union([t.undefined, t.string, t.null])
});

export type BetDataType = t.TypeOf<typeof BetIO>;
const isBetTypeIO = createGuard(BetIO);

export const LegReferenceIO = t.interface({
    geniusMarketId: t.string,
    geniusFixtureId: t.string,
});

const LegTypIO = t.interface({
    selectionId: t.union([t.number, t.undefined]),
    marketId: t.union([t.number, t.undefined]),
    eventId: t.union([t.number, t.undefined]),
    price: t.union([t.string, t.undefined, t.number, t.null]),
    isMarketSp: t.union([t.boolean, t.undefined, t.null]),
    priceType: t.union([t.string, t.undefined, t.null]),
    eachWay: t.union([t.boolean, t.undefined, t.null]),
    uid: t.string,
    type: t.union([t.string, t.undefined]),
    name: t.union([t.string, t.undefined, t.null]),
    legReference: t.union([LegReferenceIO, t.undefined, t.null]),
    provider: t.union([t.string, t.undefined, t.null]),
    internalEventId: t.union([t.number, t.undefined]),
});

const LegsTypeIO = t.record(t.string, LegTypIO);
const LegWithReferenceIO = t.type({
    legReference: LegReferenceIO,
});
type LegsType = t.TypeOf<typeof LegsTypeIO>;
type LegType = t.TypeOf<typeof LegTypIO>;
const isLegsTypeIO = createGuard(LegsTypeIO);
const hasLegReference = createGuard(LegWithReferenceIO);

const CurrencyTypeIO = t.interface({
    readOnly: t.boolean,
    hidden: t.boolean,
    value: t.string,
});

type CurrencyTypeType = t.TypeOf<typeof CurrencyTypeIO>;
const isCurrencyTypeIO = createGuard(CurrencyTypeIO);

const SportPriceLaddersTypeIO = t.record(
    t.string,
    t.interface({
        prices: t.array(
            t.interface({
                d: t.number,
                f: t.string,
                enabled: t.boolean,
            })
        ),
    })
);
type SportPriceLaddersType = t.TypeOf<typeof SportPriceLaddersTypeIO>;
const isSportPriceLaddersTypeIO = createGuard(SportPriceLaddersTypeIO);

const FocusedBetTypeIO = t.union([BetIO, t.null, t.boolean]);
type FocusedBetType = t.TypeOf<typeof FocusedBetTypeIO>;
const isFocusedBetTypeIO = createGuard(FocusedBetTypeIO);

const SafeLegTypeIO = t.intersection([
    LegTypIO,
    t.type({
        eventId: t.number,
        marketId: t.number,
        selectionId: t.number,
    }),
]);
type SafeLegType = t.TypeOf<typeof SafeLegTypeIO>;
const isSafeLegTypeIO = createGuard(SafeLegTypeIO);

const isLegTypeIO = createGuard(LegTypIO);

const FRACTIONAL_PRICE = '0/1';

export class SingleBetState {
    @observable public stakePerLine: number | string = 0;
    @observable public stakePerLineView: number | string | null = null;
    @observable public viewPrice: number | string | null = null;
    @observable public priceOverwrite: null | string | number = null;

    public constructor(
        private readonly propsIn: SingleBetPropsType | BetBuilderBetPropsType,
        protected readonly appState: AppState
    ) {
        makeAutoObservable(this);
    }

    private get isBetBuilder(): boolean {
        return this.leg?.type === 'betBuilder';
    }

    @computed public get safeLeg(): SafeLegType | null {
        return isSafeLegTypeIO(this.leg) ? this.leg : null;
    }

    @computed private get marketId(): TypeId.MarketId | null {
        const leg = this.safeLeg;
        if (leg === null) return null;

        return TypeId.newMarketId(leg.eventId, leg.marketId);
    }

    @computed private get selectionModel(): SelectionModel | null {
        if (this.marketId === null || this.safeLeg === null) {
            return null;
        }

        return this.appState.sdkStaff.models.getSelectionAndLoad(this.marketId, this.safeLeg.selectionId);
    }

    @computed public get suspendedChange(): boolean {
        if (this.isBetBuilder || this.safeLeg === null) return false;

        const selectionModel = this.selectionModel;

        if (selectionModel === null) {
            return false;
        }
        const eventModel = this.appState.sdkStaff.models.getEvent(selectionModel.eventId);
        const marketModel = this.marketId === null ? null : this.appState.sdkStaff.models.getMarket(this.marketId);

        return selectionModel.active === false || marketModel?.active === false || eventModel?.active === false;
    }

    @computed public get competitionName(): string {
        if (this.isBetBuilder || this.safeLeg === null) {
            return '';
        }

        const eventModel = this.getEventModel(this.safeLeg.eventId);

        if (eventModel !== null) {
            return this.appState.sdkStaff.models.getCompetitionModel(eventModel.competition2.toOldId())?.name ?? '';
        }
        return '';
    }

    @action public getEventModel(
        eventId: number | undefined
    ): ReturnType<typeof this.appState.sdkStaff.models.getEvent> {
        return eventId === undefined ? null : this.appState.sdkStaff.models.getEvent(eventId);
    }

    @computed public get priceFromSportPriceLadders(): Array<{ d: number; f: string; enabled: boolean }> | null {
        if (this.props === null || this.isBetBuilder || this.safeLeg === null) {
            return null;
        }
        const selectionId = this.selectionModel?.eventId;
        if (selectionId === undefined) {
            return null;
        }
        const eventModel = this.getEventModel(selectionId);
        const sport = eventModel?.sport;

        if (sport === undefined) {
            return null;
        }

        const priceListBySporty = this.sportPriceLadders[sport];

        if (priceListBySporty === undefined) {
            return null;
        }

        const enabledPrices = priceListBySporty.prices.filter((price) => price.enabled);
        return enabledPrices;
    }

    @action public getNextPriceFromLadder = (): void => {
        //TODO: refactor method in next task
        const currentPrice = this.getPrice;
        const priceList = this.priceFromSportPriceLadders;
        if (priceList === null) {
            return;
        }
        const priceExistInList = priceList.findIndex((price) => price.f === currentPrice);
        let indexOfCurrentPrice: null | number = null;
        if (priceExistInList === -1) {
            //@ts-expect-error
            const closest = priceList.reduce((prev, curr) => {
                const splitedPrice = currentPrice.split('/');
                if (splitedPrice.length > 0) {
                    const numerator = splitedPrice[0];
                    const denominator = splitedPrice[1];
                    if (numerator === undefined || denominator === undefined) {
                        return false;
                    }
                    const numberNumerator = parseInt(numerator);
                    const numberDenominator = parseInt(denominator);

                    if (isNaN(numberNumerator) || isNaN(numberDenominator)) {
                        return false;
                    }
                    const goalPrice = numberNumerator / numberDenominator;
                    //@ts-expect-error
                    const checkPrev = prev.f.split('/')[0] / prev.f.split('/')[1];
                    //@ts-expect-error
                    const checkCurr = curr.f.split('/')[0] / curr.f.split('/')[1];
                    return checkCurr - goalPrice > checkPrev - goalPrice ? prev : curr;
                } else {
                    const goalPrice = parseInt(currentPrice);
                    return curr.d - goalPrice > prev.d - goalPrice ? prev : curr;
                }
            });
            this.viewPrice = closest.f;
        } else {
            indexOfCurrentPrice = priceExistInList;
            const nextPrice = priceList[indexOfCurrentPrice - 1];
            if (nextPrice !== undefined) {
                this.viewPrice = nextPrice.f;
            }
        }
        this.onSavePriceHandler();
    };

    @action public getPrevPriceFromLadder = (): void => {
        //TODO: refactor method in next task
        const currentPrice = this.getPrice;

        const priceList = this.priceFromSportPriceLadders;
        if (priceList === null) {
            return;
        }

        const priceExistInList = priceList.findIndex((price) => price.f === currentPrice);
        let indexOfCurrentPrice: null | number = null;
        if (priceExistInList === -1) {
            //@ts-expect-error
            const closest = priceList.reduce((prev, curr) => {
                const splitedPrice = currentPrice.split('/');
                if (splitedPrice.length > 0) {
                    const numerator = splitedPrice[0];
                    const denominator = splitedPrice[1];
                    if (numerator === undefined || denominator === undefined) {
                        return false;
                    }
                    const numberNumerator = parseInt(numerator);
                    const numberDenominator = parseInt(denominator);

                    if (isNaN(numberNumerator) || isNaN(numberDenominator)) {
                        return false;
                    }
                    const goalPrice = numberNumerator / numberDenominator;
                    //@ts-expect-error
                    const checkPrev = prev.f.split('/')[0] / prev.f.split('/')[1];
                    //@ts-expect-error
                    const checkCurr = curr.f.split('/')[0] / curr.f.split('/')[1];
                    return checkCurr + goalPrice > checkPrev + goalPrice ? prev : curr;
                } else {
                    const goalPrice = parseInt(currentPrice);
                    return curr.d + goalPrice > prev.d + goalPrice ? prev : curr;
                }
            });
            this.viewPrice = closest.f;
        } else {
            indexOfCurrentPrice = priceExistInList;
            const nextPrice = priceList[indexOfCurrentPrice + 1];
            if (nextPrice !== undefined) {
                this.viewPrice = nextPrice.f;
            }
        }

        this.onSavePriceHandler();
    };

    private formatFractionalPrice = (leg: LegType | undefined): string => {
        const price = leg?.price ?? FRACTIONAL_PRICE;
        return typeof price === 'number' ? price.toFixed(2) : price;
    };

    @computed public get getPrice(): string {
        if (this.leg === null) {
            return '0';
        }

        if (this.viewPrice !== null) {
            return this.viewPrice.toString();
        }

        const bet = this.bet;
        const legUid = bet?.legs[0];
        const legs = this.legs;

        if (typeof legUid === 'string' && isLegTypeIO(legs[legUid])) {
            const legData = legs[legUid];
            const price = this.formatFractionalPrice(legData);

            if (this.isBetBuilder) {
                return price;
            }

            if (legData.priceType === 'sp') {
                return 'SP';
            }

            return price;
        }

        if (this.safeLeg === null) {
            return FRACTIONAL_PRICE;
        }

        return this.selectionModel?.price?.f ?? FRACTIONAL_PRICE;
    }

    @action public onUpdatePriceHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        this.onUpdatePrice(event.target.value);
    };

    @action public onUpdatePrice = (value: string): void => {
        this.viewPrice = value;
    };

    @action public onSavePriceHandler = (): void => {
        const bet = this.bet;
        const legs = this.legs;

        if (bet === null) {
            return;
        }

        const leg = bet.legs[0];

        let viewPrice = this.viewPrice;
        const currentPrice = leg === undefined ? 0 : legs[leg]?.price;
        if (viewPrice !== null && isNaN(parseInt(viewPrice.toString())) === false && !(viewPrice === currentPrice)) {
            if (typeof viewPrice === 'string' && !viewPrice.includes('/')) {
                //@ts-expect-error
                viewPrice = {
                    d: parseFloat(viewPrice),
                };
            }
            if (this.props !== null && leg !== undefined) {
                this.appState.reduxAction.onBetslipUpdateLeg(this.props.userId, leg, { price: viewPrice });
                this.propsIn.onLegChange(leg, { price: viewPrice });
                if (
                    this.leg !== null &&
                    !this.isBetBuilder &&
                    this.leg.eventId !== undefined &&
                    this.leg.marketId !== undefined &&
                    this.leg.selectionId !== undefined
                ) {
                    const selectionPrice = this.selectionModel?.price?.f;
                    this.priceOverwrite = selectionPrice ?? null;
                }
                this.viewPrice = null;
            }
        } else {
            this.viewPrice = currentPrice ?? 'SP';
        }
    };

    @computed public get getStakePerLine(): number | string {
        return this.stakePerLineView === null ? this.stakePerLine : this.stakePerLineView;
    }

    @action public handleChangeStake = (value: string): void => {
        const regex = /[.,]/;
        const isDecimalCheck = regex.exec(value) !== null;
        if (isDecimalCheck) {
            const splitValue = value.split(/[.,]/);

            if (splitValue.length === 2) {
                const secondSplitValue = splitValue[1] ?? null;
                if (secondSplitValue !== null && secondSplitValue.length <= 2) {
                    this.stakePerLineView = value;
                    this.setStakePerLine(value);
                }
            }
        } else {
            this.stakePerLineView = value;
            this.setStakePerLine(value);
        }
    };

    @action public setStakePerLine = (stakePerLine: string): void => {
        this.stakePerLine = stakePerLine;
    };

    @action public onChangeStakePerLine = (e: ChangeEvent<HTMLInputElement>): void => {
        this.handleChangeStake(e.target.value);
    };

    @action public saveStake = (): void => {
        if (this.stakePerLine === this.betStake) {
            this.handleChangeStake(this.betStake.toString());
        } else {
            this.propsIn.onChange({
                stakePerLine: this.stakePerLineView,
            });
        }
        this.stakePerLineView = null;
    };

    @action public cleanStake = (): void => {
        this.stakePerLine = this.stakePerLine == '0' ? '' : this.stakePerLine;
    };

    @computed public get props(): PropsInType | null {
        if (isPropsInIO(this.propsIn)) {
            return this.propsIn;
        }
        console.log('SingleBet Error - isPropsInIO', this.propsIn);
        return null;
    }

    @computed public get bet(): BetDataType | null {
        if (this.props === null) {
            return null;
        }

        if (isBetTypeIO(this.propsIn.bet)) {
            return this.propsIn.bet;
        }
        console.log('SingleBet Error - isBetTypeIO', this.propsIn.bet);
        return null;
    }

    @computed public get legs(): LegsType {
        if (this.props === null) {
            return {};
        }
        if (isLegsTypeIO(this.propsIn.legs)) {
            return this.propsIn.legs;
        }
        console.log('SingleBet Error - isLegsTypeIO', this.propsIn.legs);
        return {};
    }

    @computed public get leg(): LegType | null {
        if (this.bet === null) {
            return null;
        }
        const firstLegFromBet = this.bet.legs[0];
        if (firstLegFromBet !== undefined) {
            return this.legs[firstLegFromBet] ?? null;
        }
        return null;
    }

    @computed public get currency(): CurrencyTypeType | null {
        if (this.props === null) {
            return null;
        }

        if (isCurrencyTypeIO(this.propsIn.currency)) {
            return this.propsIn.currency;
        }
        console.log('SingleBet Error - isCurrencyTypeIO', this.propsIn.currency);
        return null;
    }

    @computed public get currencySymbol(): string {
        if (this.currency !== null) {
            if (
                this.currency.value === 'GBP' ||
                this.currency.value === 'EUR' ||
                this.currency.value === 'XTS' ||
                this.currency.value === 'CAD' ||
                this.currency.value === 'NZD'
            ) {
                return config.symbols[this.currency.value];
            }
            return this.currency.value;
        }

        return 'n/a';
    }

    @computed public get sportPriceLadders(): SportPriceLaddersType {
        if (this.props === null) {
            return {};
        }

        if (isSportPriceLaddersTypeIO(this.propsIn.sportPriceLadders)) {
            return this.propsIn.sportPriceLadders;
        }
        console.log('SingleBet Error - isSportPriceLaddersIO', this.propsIn.sportPriceLadders);
        return {};
    }

    @computed public get assignedToMe(): boolean | undefined | null {
        if (this.props === null) {
            return undefined;
        }
        return this.props.assignedToMe;
    }

    @computed public get focusedBet(): FocusedBetType {
        if (this.props === null) {
            return null;
        }
        if (isFocusedBetTypeIO(this.propsIn.focusedBet)) {
            return this.propsIn.focusedBet;
        }

        console.log('SingleBet Error - isFocusedBetTypeIO', this.propsIn.focusedBet);
        return null;
    }

    @computed public get selected(): boolean {
        if (this.props === null) {
            return false;
        }
        return this.props.selected;
    }

    @computed public get isSelectedLeg(): boolean {
        const betLeg = this.bet?.legs[0];
        return (
            this.focusedBet !== null &&
            betLeg !== undefined &&
            this.bet?.legs.length === 1 &&
            this.focusedBet !== false &&
            this.focusedBet !== true &&
            this.focusedBet.legs.includes(betLeg)
        );
    }

    @action public onSelectHandler = (): void => {
        this.propsIn.onSelect();
    };

    @action public onUnselectHandler = (): void => {
        this.propsIn.onUnselect();
    };

    @action public onRemoveHandler = (): void => {
        this.propsIn.onRemove();
    };

    @action public onAddSplitBetHandler = (): void => {
        this.propsIn.onAddSplitBet();
    };

    @action public onRemoveSplitBetHandler = (): void => {
        this.propsIn.onRemoveSplitBet();
    };

    @action public onSetChangesStatusHandler = (status: string): void => {
        this.propsIn.onSetChangesStatus(this.bet, status);
    };

    @action public onSetBetBuilderPriceStatusHandler = (status: BetBuilderPriceStatusType | null): void => {
        if ('onSetBetBuilderPriceStatus' in this.propsIn) {
            this.propsIn.onSetBetBuilderPriceStatus(status);
        }
    };

    @action public onPriceAcceptHandler = (): void => {
        if (this.leg === null) {
            return;
        }
        if (this.isBetBuilder) {
            return;
        }
        if (this.leg.eventId === undefined || this.leg.marketId === undefined || this.leg.selectionId === undefined) {
            return;
        }
        this.onSetChangesStatusHandler('price-accept');
        const selectionModel = this.selectionModel;
        const selectionPrice = selectionModel?.price?.f;
        if (selectionPrice !== undefined) {
            this.priceOverwrite = null;
            this.onUpdatePrice(selectionPrice);
            this.onSavePriceHandler();
        }
    };

    @action public onPriceOverrideHandler = (): void => {
        if (this.isBetBuilder || this.safeLeg === null) {
            return;
        }
        const selectionPrice = this.selectionModel?.price?.f;
        this.priceOverwrite = selectionPrice ?? null;
        this.onSetChangesStatusHandler('price-override');
    };

    @action public toggle = (): void => {
        if (this.props?.selected === true) {
            this.onUnselectHandler();
        } else {
            this.onSelectHandler();
        }
    };

    @computed public get betStake(): number {
        if (this.bet !== null && this.bet.stakePerLine !== null) {
            return typeof this.bet.stakePerLine === 'string'
                ? parseFloat(this.bet.stakePerLine)
                : this.bet.stakePerLine;
        }
        return 0;
    }

    @action public toggleEachWay = (): void => {
        const bet = this.bet;
        if (bet === null) {
            return;
        }
        if (this.propsIn.inPlacement() === true || this.props?.userId === undefined) {
            return;
        }
        this.appState.reduxAction.onBetslipUpdateBet(this.props.userId, bet.uid, { eachWay: !this.betEw });
        // this.propsIn.onChange(bet); - remove after full refactor
    };

    @computed public get priceWasChange(): boolean {
        if (this.isBetBuilder || this.safeLeg === null) {
            return false;
        }

        const bet = this.bet;
        const leg = bet?.legs[0];
        const legs = this.legs;

        if (leg === undefined) {
            return false;
        }
        const currentPrice = legs[leg]?.price ?? FRACTIONAL_PRICE;
        const selectionPrice = this.selectionModel?.price?.f;
        const priceWasAccepted = this.priceOverwrite === selectionPrice;

        if (priceWasAccepted) {
            return false;
        }

        return selectionPrice !== undefined && selectionPrice !== currentPrice;
    }

    @computed public get betEw(): boolean {
        const bet = this.bet;
        if (bet === null) {
            return false;
        }

        return bet.eachWay === undefined || bet.eachWay === null ? false : bet.eachWay;
    }

    @action public toggleSP = (): void => {
        if (this.props === null || this.leg === null) {
            return;
        }
        const legId = this.bet?.legs[0];
        if (legId === undefined) {
            return;
        }
        const leg = this.legs[legId];
        const priceType = leg?.priceType === 'sp' ? 'fp' : 'sp';

        this.propsIn.onLegChange(legId, {
            priceType: priceType,
        });
    };

    @computed public get betBonusEngineFreeBets(): Array<FreeBetType> {
        if (this.bet === null) {
            return [];
        }

        return this.propsIn.bonusEngineFreeBets.filter((entry) => this.bet !== null && entry.uid === this.bet.uid);
    }

    @computed public get betBuilderPriceState(): BetBuilderPriceState | null {
        const leg = this.leg;
        const bet = this.bet;

        if (leg === null || bet === null || !this.isBetBuilder || !hasLegReference(leg)) {
            return null;
        }

        const { stakePerLine } = bet;
        const stakeValue = stakePerLine === null ? 0 : parseFloat(stakePerLine.toString()) * 100;

        return new BetBuilderPriceState(this.appState.trpcClient, leg.legReference.geniusMarketId, stakeValue);
    }
}
